// Transports turn a LogRecord into side effects. The logger fans out records
// to whichever transports are registered, in order.
//
// Two transports ship by default:
//   - console      — always on (formatted; respects level filter)
//   - sentry       — registered post-Sentry-init, info+ becomes a breadcrumb,
//                    error becomes an event
//
// Add a transport here when a new sink needs logger output (datadog, loki, …).

import * as Sentry from '@sentry/vue'
import type { LogRecord, Transport } from './loggerTypes'

const formatPrefix = (record: LogRecord): string => {
  const scope = record.scope ? ` (${record.scope})` : ''
  return `[${record.timestamp}] [${record.level.toUpperCase()}]${scope}`
}

export const consoleTransport: Transport = {
  name: 'console',
  write(record) {
    const prefix = formatPrefix(record)
    const args: unknown[] = [prefix, record.message]
    if (record.context) args.push(record.context)
    if (record.error) args.push(record.error)

    // The logger IS the abstraction over console — direct calls are
    // expected here. warn/error are allowed by the project rule; debug/info
    // need an inline disable.
    switch (record.level) {
      case 'debug':
        // eslint-disable-next-line no-console
        console.debug(...args)
        break
      case 'info':
        // eslint-disable-next-line no-console
        console.info(...args)
        break
      case 'warn':
        console.warn(...args)
        break
      case 'error':
        console.error(...args)
        break
    }
  },
}

// Sentry transport — write info+ as breadcrumbs (cheap, capped at ~100 by
// Sentry) so the lead-up to an error is visible. error writes both a
// breadcrumb AND a captured event so the issue surfaces in the dashboard.
export const sentryTransport: Transport = {
  name: 'sentry',
  write(record) {
    if (record.level === 'debug') return

    // Sentry's SeverityLevel uses 'warning' rather than 'warn'.
    const sentryLevel = record.level === 'warn' ? 'warning' : record.level
    Sentry.addBreadcrumb({
      category: record.scope ?? 'app',
      level: sentryLevel,
      message: record.message,
      data: record.context as Record<string, unknown> | undefined,
    })

    if (record.level === 'error') {
      if (record.error instanceof Error) {
        Sentry.captureException(record.error, {
          extra: { message: record.message, ...record.context },
        })
      } else {
        Sentry.captureMessage(record.message, {
          level: 'error',
          extra: { error: record.error, ...record.context },
        })
      }
    }
  },
}
